import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';

interface SubClaim { id: string; name: string; }
interface ClaimNode { id: string; name: string; subClaims: SubClaim[]; }
interface ClaimGroup {
  id: string;
  name: string;
  field_visibility?: Record<string, string>;
  custom_field_1_label?: string;
  custom_field_2_label?: string;
  custom_field_3_label?: string;
  custom_field_4_label?: string;
  custom_field_5_label?: string;
  claims: ClaimNode[];
}

interface ClaimRow {
  id: string;
  claim_number: string;
  employee_id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  claim_group_name: string;
  claim_name: string;
  sub_claim_name: string | null;
  total_amount: number;
  status: string;
  cancellation_status: string;
  receipt_date: string;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const TABS = [
  { key: 'pending-for-me', label: 'Pending for Me' },
  { key: 'my-decisions', label: 'My Approved/Rejected' },
  { key: 'pending-previous-level', label: 'Pending Previous Level' },
];

const EMPTY_FORM = {
  employeeId: '',
  claimGroupId: '',
  claimMasterId: '',
  subClaimId: '',
  totalAmount: '',
  receiptDate: '',
  description: '',
  customField1: '', customField2: '', customField3: '', customField4: '', customField5: '',
};

export default function ExpenseClaims() {
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [groups, setGroups] = useState<ClaimGroup[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [approvalTab, setApprovalTab] = useState('pending-for-me');
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [decisions, setDecisions] = useState<any[]>([]);
  const [uploadTarget, setUploadTarget] = useState<ClaimRow | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ClaimRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  useEffect(() => {
    fetchClaims();
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchApprovalTab(approvalTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalTab]);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecs/claims');
      setClaims(response.data.claims || []);
    } catch (error) {
      console.error('Failed to fetch claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    const res = await api.get('/ecs/masters/claim-groups');
    setGroups(res.data.claimGroups || []);
  };

  const fetchApprovalTab = async (tab: string) => {
    try {
      if (tab === 'my-decisions') {
        const res = await api.get('/ecs/claims/my-decisions');
        setDecisions(res.data.claims || []);
        setPendingApprovals([]);
      } else {
        const res = await api.get(`/ecs/claims/${tab}`);
        setPendingApprovals(res.data.approvals || []);
        setDecisions([]);
      }
    } catch (error) {
      console.error('Failed to fetch approval tab:', error);
    }
  };

  const selectedGroup = useMemo(() => groups.find((g) => g.id === formData.claimGroupId), [groups, formData.claimGroupId]);
  const selectedClaim = useMemo(() => selectedGroup?.claims.find((c) => c.id === formData.claimMasterId), [selectedGroup, formData.claimMasterId]);
  const visibility = selectedGroup?.field_visibility || {};
  const isVisible = (key: string) => visibility[key] !== 'HIDE';
  const isMandatory = (key: string) => visibility[key] === 'MANDATORY';
  const customFieldLabels = [
    selectedGroup?.custom_field_1_label, selectedGroup?.custom_field_2_label, selectedGroup?.custom_field_3_label,
    selectedGroup?.custom_field_4_label, selectedGroup?.custom_field_5_label,
  ];

  const handleSubmitDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/ecs/claims', formData);
      setFormData({ ...EMPTY_FORM });
      setShowForm(false);
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create claim');
    }
  };

  const handleDeleteDraft = async (claimId: string) => {
    if (!window.confirm('Delete this draft claim?')) return;
    try {
      await api.delete(`/ecs/claims/${claimId}`);
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete draft');
    }
  };

  const handleSubmitSelected = async () => {
    const claimIds = Object.keys(selected).filter((id) => selected[id]);
    if (!claimIds.length) return;
    try {
      const res = await api.put('/ecs/claims/submit', { claimIds });
      const failures = (res.data.results || []).filter((r: any) => r.error);
      if (failures.length) {
        alert(`${failures.length} claim(s) failed to submit:\n` + failures.map((f: any) => f.error).join('\n'));
      } else {
        alert('Claim(s) submitted for approval');
      }
      setSelected({});
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to submit claims');
    }
  };

  const handleApprovalAction = async (claimId: string, action: 'APPROVED' | 'REJECTED') => {
    try {
      await api.put(`/ecs/claims/${claimId}/approve`, { action });
      fetchApprovalTab(approvalTab);
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to act on approval');
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason) return;
    try {
      const res = await api.put(`/ecs/claims/${cancelTarget.id}/cancel`, { reason: cancelReason });
      alert(res.data.message);
      setCancelTarget(null);
      setCancelReason('');
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to cancel claim');
    }
  };

  const handleUpload = async () => {
    if (!uploadTarget || !uploadFile) return;
    const form = new FormData();
    form.append('file', uploadFile);
    form.append('fileType', 'RECEIPT');
    try {
      await api.post(`/ecs/claims/${uploadTarget.id}/attachments/upload`, form, {
        headers: { 'Content-Type': undefined },
      });
      alert('Receipt uploaded');
      setUploadTarget(null);
      setUploadFile(null);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Upload failed (check that file storage is configured)');
    }
  };

  const columns: Column<ClaimRow>[] = [
    {
      key: 'select', header: '',
      render: (row) => row.status === 'DRAFT' ? (
        <input type="checkbox" checked={!!selected[row.id]} onChange={(e) => setSelected({ ...selected, [row.id]: e.target.checked })} />
      ) : null,
    },
    { key: 'claim_number', header: 'Claim No.' },
    { key: 'employee', header: 'Employee', render: (row) => `${row.first_name} ${row.last_name}` },
    {
      key: 'claim_hierarchy', header: 'Claim',
      render: (row) => [row.claim_group_name, row.claim_name, row.sub_claim_name].filter(Boolean).join(' → '),
    },
    { key: 'receipt_date', header: 'Receipt Date', render: (row) => row.receipt_date ? new Date(row.receipt_date).toLocaleDateString() : '—' },
    { key: 'total_amount', header: 'Amount', render: (row) => `$${Number(row.total_amount).toLocaleString()}` },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'cancellation_status', header: 'Cancellation',
      render: (row) => (row.cancellation_status && row.cancellation_status !== 'NONE' ? <StatusBadge status={row.cancellation_status} /> : '—'),
    },
    {
      key: 'actions', header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          {row.status === 'DRAFT' && (
            <button onClick={() => handleDeleteDraft(row.id)} className="text-red-600 hover:underline text-sm">Delete</button>
          )}
          {(row.status === 'SUBMITTED' || row.status === 'APPROVED') && row.cancellation_status !== 'PENDING' && (
            <button onClick={() => setCancelTarget(row)} className="text-red-600 hover:underline text-sm">Cancel</button>
          )}
          <button onClick={() => setUploadTarget(row)} className="text-gray-600 hover:underline text-sm">Attach Receipt</button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Claim Applications</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleSubmitSelected} disabled={!Object.values(selected).some(Boolean)}>
            Submit Selected
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'New Draft Claim'}</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setApprovalTab(t.key)}
              className={`px-4 py-3 text-sm font-medium ${approvalTab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-4">
          {approvalTab === 'my-decisions' ? (
            decisions.length === 0 ? (
              <p className="text-sm text-gray-500">No decisions yet.</p>
            ) : (
              <ul className="space-y-2">
                {decisions.map((d: any) => (
                  <li key={d.id} className="flex justify-between items-center bg-gray-50 rounded-lg p-3 text-sm">
                    <span>
                      {d.first_name} {d.last_name} ({d.employee_code}) — <strong>{d.claim_number}</strong>, ${Number(d.total_amount).toLocaleString()}
                    </span>
                    <StatusBadge status={d.status} />
                  </li>
                ))}
              </ul>
            )
          ) : pendingApprovals.length === 0 ? (
            <p className="text-sm text-gray-500">Nothing here.</p>
          ) : (
            <ul className="space-y-2">
              {pendingApprovals.map((row: any) => (
                <li key={row.id} className="flex justify-between items-center bg-amber-50 rounded-lg p-3 text-sm">
                  <span>
                    {row.first_name} {row.last_name} ({row.employee_code}) — <strong>{row.claim_number}</strong>,{' '}
                    ${Number(row.total_amount).toLocaleString()} — level {row.approval_level}/{row.approval_final_level}
                  </span>
                  {approvalTab === 'pending-for-me' && (
                    <div className="flex gap-2">
                      <button onClick={() => handleApprovalAction(row.id, 'APPROVED')} className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">Approve</button>
                      <button onClick={() => handleApprovalAction(row.id, 'REJECTED')} className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700">Reject</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmitDraft} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <LookupSelect
                label="Employee"
                value={formData.employeeId}
                onChange={(value) => setFormData({ ...formData, employeeId: value })}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search employee..."
                required
              />
              <Select
                label="Claim Group"
                value={formData.claimGroupId}
                onChange={(e) => setFormData({ ...formData, claimGroupId: e.target.value, claimMasterId: '', subClaimId: '' })}
                options={groups.map((g) => ({ value: g.id, label: g.name }))}
                required
              />
              <Select
                label="Claim"
                value={formData.claimMasterId}
                onChange={(e) => setFormData({ ...formData, claimMasterId: e.target.value, subClaimId: '' })}
                options={(selectedGroup?.claims || []).map((c) => ({ value: c.id, label: c.name }))}
                required
              />
              {!!selectedClaim?.subClaims.length && (
                <Select
                  label="Sub-Claim (optional)"
                  value={formData.subClaimId}
                  onChange={(e) => setFormData({ ...formData, subClaimId: e.target.value })}
                  options={[{ value: '', label: '— None —' }, ...selectedClaim.subClaims.map((sc) => ({ value: sc.id, label: sc.name }))]}
                />
              )}
              <Input
                label="Amount"
                type="number" step="0.01"
                value={formData.totalAmount}
                onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                required
              />
              {isVisible('receiptDate') && (
                <Input
                  label={`Receipt Date${isMandatory('receiptDate') ? ' *' : ''}`}
                  type="date"
                  value={formData.receiptDate}
                  onChange={(e) => setFormData({ ...formData, receiptDate: e.target.value })}
                  required={isMandatory('receiptDate')}
                />
              )}
              {isVisible('description') && (
                <Input
                  label={`Description${isMandatory('description') ? ' *' : ''}`}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required={isMandatory('description')}
                  className="col-span-2"
                />
              )}
              {customFieldLabels.map((label, i) =>
                label && isVisible(`customField${i + 1}`) ? (
                  <Input
                    key={i}
                    label={`${label}${isMandatory(`customField${i + 1}`) ? ' *' : ''}`}
                    value={(formData as any)[`customField${i + 1}`]}
                    onChange={(e) => setFormData({ ...formData, [`customField${i + 1}`]: e.target.value })}
                    required={isMandatory(`customField${i + 1}`)}
                  />
                ) : null
              )}
            </div>
            <Button type="submit" className="w-full">Save Draft</Button>
          </form>
        </div>
      )}

      <Table columns={columns} rows={claims} loading={loading} emptyMessage="No claims yet" />

      <Modal isOpen={!!uploadTarget} onClose={() => setUploadTarget(null)} title="Attach Receipt">
        <input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="w-full border rounded-lg p-2 mb-4" />
        <Button onClick={handleUpload} disabled={!uploadFile} className="w-full">Upload</Button>
      </Modal>

      <Modal isOpen={!!cancelTarget} onClose={() => setCancelTarget(null)} title="Cancel Claim">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {cancelTarget?.status === 'APPROVED'
              ? 'This is an approved claim — cancellation requires approver sign-off per the configured Cancellation Approver Mode.'
              : 'This pending claim will be cancelled immediately.'}
          </p>
          <Input label="Cancellation Reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} required />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelTarget(null)}>Close</Button>
            <Button onClick={handleCancel}>Submit Cancellation</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
